'use client';

import Attach from '@/assets/svg/Attach';
import AttachImg from '@/assets/svg/Attach';
import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { IComponentProps } from '@/interfaces/component.interface';
import { ArrowRightIcon } from 'lucide-react';
import { sendEmail } from '@/actions/email.actions';
import uploadFile from '@/actions/upload.actions';

export default function ContactForm(props: IComponentProps) {
  const { text } = props;

  const [fileLocation, setFileLocation] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<boolean>(false);
  const [fileLoading, setFileLoading] = useState<boolean>(false);

  const formSchema = z.object({
    expertise: z.string().min(2),
    name: z.string().min(2),
    email: z.string().email().min(5),
    message: z.string().min(10),
    portfolio: z.string().min(2),
    socialMedia: z.string().optional(),
    curriculum: z.any(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    sendEmail({
      ...values,
      curriculum: fileLocation || 'No file uploaded',
    })
      .then(() => setSuccessMessage(true))
      .catch((err) => {
        setErrorMessage(true);
        console.error(err);
      });
  }

  async function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
    setFileLoading(true);
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const buffer = await file.arrayBuffer();
      const formData = new FormData();
      formData.append('file', new Blob([buffer]), 'file');
      const response = await uploadFile(formData);

      if (!response.success) setErrorMessage(true);
      else setFileLocation(response.location!);

      setFileLoading(false);
    } catch (error) {
      console.error(error);
      setFileLoading(false);
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='space-y-[20px] md:space-y-[56px] 2xl:text-[20px] text-[13px] text-black mt-[56px] sm:mt-0 mb-[196px] sm:mb-32'
      >
        <FormField
          control={form.control}
          name={text.expertise.field}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder={text.expertise.placeholder} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={text.fullName.field}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder={text.fullName.placeholder} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={text.email.field}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  type='email'
                  placeholder={text.email.placeholder}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={text.message.field}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea placeholder={text.message.placeholder} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={text.portfolio.field}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder={text.portfolio.placeholder} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={text.socialMedia.field}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder={text.socialMedia.placeholder} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={text.curriculum.field}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <label>
                  <div className='flex  gap-2 items-center border-b py-3 border-[#0D152499]/50'>
                    <div className='w-6 h-6 sm:w-4 sm:h-4'>
                      <AttachImg />
                    </div>
                    <p className='text-[20px] sm:text-[14px] leading-6 font-bold '>
                      {text.curriculum.placeholder}
                    </p>
                  </div>
                  <Input
                    type='file'
                    accept='application/pdf'
                    className='hidden'
                    {...field}
                    onChange={(e: any) => handleFileUpload(e)}
                  />
                </label>
              </FormControl>
              <FormDescription>
                <p className='text-[20px] sm:text-[14px] leading-[120%] tracking-[-0.03]'>
                  {text.curriculum.description1}
                  <br />
                  {text.curriculum.description2}
                </p>
                {fileLoading && (
                  <div className='mt-3 text-xl'>Uploading file...</div>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type='submit'
          variant='link'
          className='rounded-full !mt-16 px-0 2xl:text-[64px] text-[42px] sm:text-[32px] leading-none tracking-[-0.05em] md:text-[68px] flex items-center gap-2 pt-4'
        >
          <div className='relative'>
            {text.submit}
            <div className='border-b h-[1px] border-[#0D1524] mt-1' />
          </div>
          <ArrowRightIcon size={49} />
        </Button>

        {successMessage && (
          <p className='my-7 text-green-400'>{text.successMessage}</p>
        )}
        {errorMessage && (
          <p className='my-7 text-red-400'>{text.errorMessage}</p>
        )}
      </form>
    </Form>
  );
}
